import { c as _c } from "react/compiler-runtime";
import { Icon } from '@udemy-v2/icons';
import { Button, IconButton } from '@udemy-v2/react-core-components';
import { BottomDrawer, Modal } from '@udemy-v2/react-dialog-components';
import { Loader } from '@udemy-v2/react-reveal-components';
import React, { useCallback, useState } from 'react';
import { useDeviceType } from '@udemy-v2/hooks';
import { useI18n } from '@udemy-v2/i18n';
import { SOURCE_PAGES } from './constants';
import { isMarketplaceShareEnabled } from './helpers';
import { useSocialShare } from './hooks/use-social-share';
import { ShareableObject, ChannelType } from './types';
import { CourseShareEvent } from '../../events/events';
import { publishEvent } from '@udemy-v2/event-tracking';
type ButtonStyle = 'primary' | 'ghost' | 'secondary' | 'knockout-outline' | 'knockout-solid';
type Size = 'small' | 'medium' | 'large';
export interface ShareContentProps {
  isImportEmailModalShown: boolean;
  sharedUrl: string;
  hideImportEmailModal: () => void;
  onFormCancel: () => void;
  share: (channel: ChannelType, onError?: (error: Error) => void) => Promise<void>;
  sourcePage: string;
  shareableObject: ShareableObject;
}
export interface LoadedModule {
  default: React.ComponentType<ShareContentProps>;
}
export interface MarketplaceSocialShareButtonProps {
  /** Button style variant */
  buttonStyle?: ButtonStyle;
  /** Context where the share button is used */
  context: string;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** Function to lazily load the share content */
  load: () => Promise<LoadedModule>;
  /** Object to be shared */
  shareableObject: ShareableObject;
  /** URL to be shared */
  sourceUrl: string;
  /** Whether the button should be round */
  round?: boolean;
  /** Whether the button should show as only an icon */
  onlyIcon?: boolean;
  /** Custom button text */
  buttonText?: string;
  /** Whether to show the share icon */
  isIconVisible?: boolean;
  /** Additional class name */
  className?: string;
  /** Button size */
  size?: Size;
}
export const MarketplaceSocialShareButton: React.FC<MarketplaceSocialShareButtonProps> = t0 => {
  const $ = _c(49);
  const {
    buttonStyle: t1,
    context,
    fullWidth: t2,
    load,
    shareableObject,
    sourceUrl,
    round: t3,
    onlyIcon: t4,
    buttonText,
    isIconVisible: t5,
    className,
    size: t6
  } = t0;
  const buttonStyle = t1 === undefined ? "ghost" : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const round = t3 === undefined ? false : t3;
  const onlyIcon = t4 === undefined ? false : t4;
  const isIconVisible = t5 === undefined ? false : t5;
  const size = t6 === undefined ? "medium" : t6;
  const [isShown, setIsShown] = useState(false);
  const {
    gettext
  } = useI18n();
  const deviceType = useDeviceType();
  let t7;
  if ($[0] !== context || $[1] !== shareableObject || $[2] !== sourceUrl) {
    t7 = {
      shareableObject,
      sourceUrl,
      context
    };
    $[0] = context;
    $[1] = shareableObject;
    $[2] = sourceUrl;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const socialShare = useSocialShare(t7);
  let t8;
  if ($[4] !== load) {
    t8 = () => React.lazy(() => load());
    $[4] = load;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const [LazyShareContent] = useState(t8);
  let t9;
  if ($[6] !== gettext || $[7] !== shareableObject.isFree || $[8] !== socialShare.isImportEmailModalShown || $[9] !== socialShare.sharedUrl) {
    t9 = () => {
      if (!socialShare.sharedUrl) {
        return gettext("Loading");
      }
      if (socialShare.isImportEmailModalShown) {
        return gettext("Share via email");
      }
      if (shareableObject.isFree) {
        return gettext("Share");
      }
      return gettext("Share this course");
    };
    $[6] = gettext;
    $[7] = shareableObject.isFree;
    $[8] = socialShare.isImportEmailModalShown;
    $[9] = socialShare.sharedUrl;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const getTitle = t9;
  let t10;
  if ($[11] !== context || $[12] !== shareableObject.id || $[13] !== socialShare) {
    t10 = async () => {
      if (context === SOURCE_PAGES.CLP) {
        publishEvent(new CourseShareEvent({
          courseId: parseInt(shareableObject.id, 10)
        }));
      }
      setIsShown(true);
      if (!socialShare.sharedUrl) {
        await socialShare.getOrPopulateUrl();
      }
    };
    $[11] = context;
    $[12] = shareableObject.id;
    $[13] = socialShare;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const openContainer = t10;
  let t11;
  if ($[15] !== socialShare) {
    t11 = () => {
      setIsShown(false);
      socialShare.hideImportEmailModal();
    };
    $[15] = socialShare;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const closeContainer = t11;
  if (!isMarketplaceShareEnabled(shareableObject)) {
    return null;
  }
  const Dialog = deviceType === "desktop" ? Modal : BottomDrawer;
  let t12;
  if ($[17] !== buttonText || $[18] !== gettext) {
    t12 = buttonText || gettext("Share");
    $[17] = buttonText;
    $[18] = gettext;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const shareText = t12;
  let t13;
  if ($[20] !== buttonStyle || $[21] !== className || $[22] !== fullWidth || $[23] !== isIconVisible || $[24] !== onlyIcon || $[25] !== openContainer || $[26] !== round || $[27] !== shareText || $[28] !== size) {
    t13 = round || onlyIcon ? <IconButton onClick={openContainer} udStyle={buttonStyle} round={round} size={size} data-purpose="social-share-button" className={className} aria-label={shareText}><Icon glyph="share" data-testid="share-icon" aria-hidden="true" label={shareText} /></IconButton> : <Button componentClass="button" onClick={openContainer} size={size} style={fullWidth ? {
      width: "100%"
    } : {}} udStyle={buttonStyle} data-purpose="social-share-button" className={className} aria-label={shareText}><span>{shareText}</span>{isIconVisible && <Icon glyph="share" data-testid="share-icon" aria-hidden="true" label={shareText} />}</Button>;
    $[20] = buttonStyle;
    $[21] = className;
    $[22] = fullWidth;
    $[23] = isIconVisible;
    $[24] = onlyIcon;
    $[25] = openContainer;
    $[26] = round;
    $[27] = shareText;
    $[28] = size;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== getTitle) {
    t14 = getTitle();
    $[30] = getTitle;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== LazyShareContent || $[33] !== context || $[34] !== shareableObject || $[35] !== socialShare.hideImportEmailModal || $[36] !== socialShare.isImportEmailModalShown || $[37] !== socialShare.share || $[38] !== socialShare.sharedUrl) {
    t15 = socialShare.sharedUrl ? <React.Suspense fallback={<Loader block={true} size="medium" />}><LazyShareContent onFormCancel={socialShare.hideImportEmailModal} isImportEmailModalShown={socialShare.isImportEmailModalShown} sharedUrl={socialShare.sharedUrl} hideImportEmailModal={socialShare.hideImportEmailModal} share={socialShare.share} sourcePage={context} shareableObject={shareableObject} /></React.Suspense> : <Loader block={true} size="medium" />;
    $[32] = LazyShareContent;
    $[33] = context;
    $[34] = shareableObject;
    $[35] = socialShare.hideImportEmailModal;
    $[36] = socialShare.isImportEmailModalShown;
    $[37] = socialShare.share;
    $[38] = socialShare.sharedUrl;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== Dialog || $[41] !== closeContainer || $[42] !== isShown || $[43] !== t14 || $[44] !== t15) {
    t16 = <Dialog isOpen={isShown} onClose={closeContainer} title={t14}>{t15}</Dialog>;
    $[40] = Dialog;
    $[41] = closeContainer;
    $[42] = isShown;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t13 || $[47] !== t16) {
    t17 = <>{t13}{t16}</>;
    $[46] = t13;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};